import { createContext, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import type {
  DrawResponse,
  StandingsResponse,
  GroupStandings,
  StandingRow,
  BracketMatchResponse,
} from "@bw/shared/types/generated";
import { useTournamentDraws } from "../hooks/useTournamentDraws";
import { useDrawStandings } from "../hooks/useDrawStandings";
import { useDrawBracket } from "../hooks/useDrawBracket";
import { TeamDrawResults } from "./TeamDrawResults";
import { BracketMatchCard } from "./BracketMatchCard";
import { DrawRounds } from "./DrawRounds";
import { PillChip } from "../../../common/components/PillChip";
import { visuallyHidden } from "../../../common/visuallyHidden";

/** Per-draw presentation facts shared by every nested bracket/standings view. */
const DrawView = createContext<{
  myEntryIds: ReadonlySet<string>;
  mode: "draws" | "results";
}>({ myEntryIds: new Set(), mode: "draws" });

const NO_ENTRIES: ReadonlySet<string> = new Set();

/** "main_draw" → "Main draw"; "16 teams" for partner draws, else "players". */
function drawMeta(draw: DrawResponse): string {
  const stage = draw.is_consolation
    ? (draw.consolation_label || "Consolation")
    : humanize(draw.stage ?? "main_draw");
  const count = draw.accepted_entries;
  const unit = draw.requires_partner
    ? count === 1 ? "team" : "teams"
    : count === 1 ? "player" : "players";
  return [stage, count > 0 ? `${count} ${unit}` : null, humanize(draw.format)]
    .filter(Boolean)
    .join(" · ");
}

const PLACES_COLUMN_PAGE_SIZE = 24;

type PlacesColumn = {
  phase: string | null | undefined;
  phaseLabel: string | null | undefined;
  roundNumber: number;
  matches: BracketMatchResponse[];
};

/**
 * The Draws section of the read-only tournament detail. Its OWN react-query hook
 * (useTournamentDraws) keeps it ISOLATED from the main detail fetch — a failing
 * draws fetch surfaces THIS section's role="alert" while the page's name /
 * schedule (and the Events section) still render (never a blank page). The error
 * state is gated on the query's `isError`, not `!data`.
 *
 * Every value comes from the backend VERBATIM. The section is a THIN presentation
 * layer with zero business logic:
 *   - the STATUS + FORMAT are humanized from the raw enum tokens
 *     ("in_progress" → "In progress", "single_elimination" → "Single
 *     elimination") — never the lowercase token echoed as a visible label.
 *   - a draw's `draw_id` is NEVER rendered as text (it is only a select key).
 *   - selecting a draw mounts the detail for its format: round_robin →
 *     <DrawStandings> (getStandings), elimination formats → <DrawBracket>
 *     (getBracket), and group-qualified formats → both canonical projections.
 *   - standings rows render the server's rank / display_name / W-L / diffs
 *     VERBATIM — the page never re-ranks, re-sorts, or recomputes a diff, and a
 *     row's rank is row.rank (the server value), NOT its array index.
 */
export function TournamentDrawsSection({
  tournamentId,
  tournamentType,
  mode = "draws",
  myEntryIds = NO_ENTRIES,
}: {
  tournamentId: string;
  tournamentType: string;
  /** The viewer's active entry ids in this tournament (their matches are outlined). */
  myEntryIds?: ReadonlySet<string>;
  /** Results stacks every round and leads with standings. */
  mode?: "draws" | "results";
}) {
  const { data, isLoading, isError } = useTournamentDraws(tournamentId);
  const draws = data ?? [];
  // Track the selected draw by its id; the matching DrawResponse drives which
  // detail (standings vs bracket) is mounted.
  const [params, setParams] = useSearchParams();
  const requestedDraw = params.get("draw_id");
  const selectedDraw = requestedDraw
    ? (draws.find((draw) => draw.draw_id === requestedDraw) ?? null)
    : (draws[0] ?? null);
  const selectedDrawId = selectedDraw?.draw_id;
  const selectDraw = (drawId: string | null) => {
    const next = new URLSearchParams(params);
    next.set("tab", mode);
    if (drawId) next.set("draw_id", drawId);
    else next.delete("draw_id");
    next.delete("column");
    next.delete("places_page");
    next.delete("team_match_page");
    setParams(next);
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Typography color="text.secondary" role="status">
        Loading draws…
      </Typography>
    );
  } else if (isError) {
    body = (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the draws. Please try again.
      </Alert>
    );
  } else if (draws.length === 0) {
    body = <Typography color="text.secondary">No draws yet</Typography>;
  } else {
    body = (
      <Stack spacing={1.5}>
        {requestedDraw && !selectedDraw ? (
          <Alert
            severity="warning"
            action={
              <Button onClick={() => selectDraw(null)}>
                Show available draws
              </Button>
            }
          >
            This draw is unavailable in this tournament.
          </Alert>
        ) : null}
        {draws.length > 0 ? (
          <Stack
            role="group"
            aria-label="Draw"
            direction="row"
            spacing={0.75}
            sx={{ overflowX: "auto", scrollbarWidth: "none", pb: 0.25 }}
          >
            {draws.map((draw) => (
              <PillChip
                key={draw.draw_id}
                selected={draw.draw_id === selectedDrawId}
                onClick={() => selectDraw(draw.draw_id)}
                ariaLabel={`${draw.name} · ${humanize(draw.format)} · ${humanize(draw.status)}`}
              >
                {draw.name}
              </PillChip>
            ))}
          </Stack>
        ) : null}
        {selectedDraw ? (
          <>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
              {drawMeta(selectedDraw)}
            </Typography>
            <DrawView.Provider
              value={{
                myEntryIds,
                mode,
              }}
            >
              {tournamentType === "team" ? (
                <TeamDrawResults
                  key={selectedDraw.draw_id}
                  drawId={selectedDraw.draw_id}
                />
              ) : tournamentType === "individual" ? (
                <DrawDetail key={selectedDraw.draw_id} draw={selectedDraw} />
              ) : (
                <Typography>This tournament format is unavailable.</Typography>
              )}
            </DrawView.Provider>
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <Box component="section" aria-label={mode === "results" ? "Results" : "Draws"}>
      <Typography component="h2" sx={visuallyHidden}>
        {mode === "results" ? "Results" : "Draws"}
      </Typography>
      {body}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Humanize raw enum tokens → "In progress", "Single elimination", "Round robin"
// (replace underscores with spaces, capitalize the first letter; never echo the
// raw token).
// ---------------------------------------------------------------------------

function humanize(token: string | null | undefined): string {
  if (!token) return "";
  if (token === "single_elimination_consolation") {
    return "Single elimination with consolation";
  }
  if (token === "places_playout") {
    return "Places play-out";
  }
  if (token === "third_place_playoff") {
    return "Third-place playoff";
  }
  if (token === "abcd_flights") {
    return "ABCD drop flight";
  }
  const spaced = token.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------------------------------------------------------------------------
// Per-draw detail. Group-qualified formats mount BOTH public reads: pools
// establish qualification and the same draw's phase="knockout" rows are the
// downstream bracket. Neither is derived here.
// ---------------------------------------------------------------------------

function DrawDetail({ draw }: { draw: DrawResponse }) {
  if (draw.format === "round_robin") {
    return <DrawStandings drawId={draw.draw_id} />;
  }
  if (draw.format === "single_elimination") {
    return <DrawBracket drawId={draw.draw_id} />;
  }
  if (draw.format === "group_to_knockout") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The group stage has not been generated yet.
        </Typography>
      );
    }
    return <GroupToKnockoutDetail draw={draw} />;
  }
  if (draw.format === "guaranteed_minimum_matches") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The guaranteed-match draw has not been generated yet.
        </Typography>
      );
    }
    return <GuaranteedMinimumMatchesDetail draw={draw} />;
  }
  if (draw.format === "double_elimination") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The double-elimination bracket has not been generated yet.
        </Typography>
      );
    }
    return <DoubleEliminationDetail draw={draw} />;
  }
  if (draw.format === "single_elimination_consolation") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The main and consolation brackets have not been generated yet.
        </Typography>
      );
    }
    return <SingleEliminationConsolationDetail draw={draw} />;
  }
  if (draw.format === "places_playout") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The full placement bracket has not been generated yet.
        </Typography>
      );
    }
    return <PlacesPlayoutDetail key={draw.draw_id} draw={draw} />;
  }
  if (draw.format === "third_place_playoff") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The main and third-place playoff brackets have not been generated yet.
        </Typography>
      );
    }
    return <ThirdPlacePlayoffDetail draw={draw} />;
  }
  if (draw.format === "abcd_flights") {
    if (draw.bracket_match_count === 0) {
      return (
        <Typography color="text.secondary">
          The ABCD placement and flight brackets have not been generated yet.
        </Typography>
      );
    }
    return <PlacesPlayoutDetail key={draw.draw_id} draw={draw} />;
  }
  // Any other format has no public read here — show nothing rather than guess.
  return null;
}

function GuaranteedMinimumMatchesDetail({ draw }: { draw: DrawResponse }) {
  const guarantee = draw.minimum_matches ?? null;

  return (
    <Stack spacing={2}>
      {guarantee !== null ? (
        <Typography color="text.secondary" variant="body2">
          Minimum scheduled matches: {guarantee}
        </Typography>
      ) : null}
      {draw.group_count <= 1 ? (
        <DrawStandings drawId={draw.draw_id} />
      ) : (
        <GroupToKnockoutDetail draw={draw} />
      )}
    </Stack>
  );
}

function DoubleEliminationDetail({ draw }: { draw: DrawResponse }) {
  const { data, isLoading, isError } = useDrawBracket(draw.draw_id);

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading bracket…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the bracket. Please try again.
      </Alert>
    );
  }

  const matches = data ?? [];
  const matchesFor = (phase: string) =>
    matches.filter((match) => match.phase === phase);
  const resetMatches = matchesFor("grand_final_reset");
  const resetWasCancelled = resetMatches.some(
    (match) => match.status === "cancelled",
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          Winners bracket
        </Typography>
        <BracketMatchList matches={matchesFor("winner_bracket")} />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          Losers bracket
        </Typography>
        <BracketMatchList matches={matchesFor("loser_bracket")} />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          Grand final
        </Typography>
        <BracketMatchList matches={matchesFor("grand_final")} />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
          Grand final reset
        </Typography>
        {resetWasCancelled ? (
          <Alert severity="info" role="status" sx={{ mb: 1.5 }}>
            Not required: the first grand final decided the draw.
          </Alert>
        ) : (
          <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
            Played only if the lower-bracket champion wins the first grand
            final.
          </Typography>
        )}
        <BracketMatchList matches={resetMatches} />
      </Box>
    </Stack>
  );
}

function SingleEliminationConsolationDetail({ draw }: { draw: DrawResponse }) {
  const { data, isLoading, isError } = useDrawBracket(draw.draw_id);

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading bracket…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the bracket. Please try again.
      </Alert>
    );
  }

  const matches = data ?? [];
  return (
    <Stack spacing={3}>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          Main bracket
        </Typography>
        <BracketMatchList
          matches={matches.filter((match) => match.phase === "main")}
        />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
          Consolation
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
          Opening-round main-bracket losers play for the consolation title.
        </Typography>
        <BracketMatchList
          matches={matches.filter((match) => match.phase === "consolation")}
        />
      </Box>
    </Stack>
  );
}

function ThirdPlacePlayoffDetail({ draw }: { draw: DrawResponse }) {
  const { data, isLoading, isError } = useDrawBracket(draw.draw_id);

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading bracket…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the bracket. Please try again.
      </Alert>
    );
  }

  const matches = data ?? [];
  const mainMatches = matches.filter((match) => match.phase === "main");
  const playoffMatches = matches.filter(
    (match) => match.phase === "third_place",
  );
  const mainLabel = mainMatches[0]?.phase_label ?? "Main bracket";
  const playoffLabel = playoffMatches[0]?.phase_label ?? "Third-place playoff";

  return (
    <Stack spacing={3}>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          {mainLabel}
        </Typography>
        <BracketMatchList matches={mainMatches} />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
          {playoffLabel}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
          The two semifinal losers play for third and fourth place.
        </Typography>
        <BracketMatchList matches={playoffMatches} />
      </Box>
    </Stack>
  );
}

function placesColumnKey(
  phase: string | null | undefined,
  roundNumber: number,
): string {
  return `${phase ?? "main"}:${roundNumber}`;
}

function placesColumnLabel(column: PlacesColumn): string {
  const phase = column.phase ?? "main";
  const placesPhase = phase.match(/^places_(\d+)_(\d+)$/);
  const phaseLabel =
    column.phaseLabel ??
    (placesPhase
      ? `Places ${placesPhase[1]}–${placesPhase[2]}`
      : "Main bracket");
  return `${phaseLabel} · Round ${column.roundNumber}`;
}

function PlacesPlayoutDetail({ draw }: { draw: DrawResponse }) {
  const { data, isLoading, isError } = useDrawBracket(draw.draw_id);
  const [params, setParams] = useSearchParams();
  const selectedColumnKey = params.get("column");
  const rawPage = Number(params.get("places_page"));
  const requestedPage =
    Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;

  const columns = useMemo(() => {
    const grouped = new Map<string, PlacesColumn>();
    for (const match of data ?? []) {
      const phase = match.phase ?? "main";
      const key = placesColumnKey(phase, match.round_number);
      const column = grouped.get(key) ?? {
        phase,
        phaseLabel: match.phase_label ?? null,
        roundNumber: match.round_number,
        matches: [],
      };
      column.matches.push(match);
      grouped.set(key, column);
    }
    // The API already gives the canonical graph order. This only groups its
    // server-owned phase/round coordinates for navigation; it never derives a
    // route, winner, or placement client-side.
    return Array.from(grouped.values());
  }, [data]);
  const selectedColumn =
    columns.find(
      (column) =>
        placesColumnKey(column.phase, column.roundNumber) === selectedColumnKey,
    ) ??
    columns[0] ??
    null;
  const pageCount = selectedColumn
    ? Math.max(
        1,
        Math.ceil(selectedColumn.matches.length / PLACES_COLUMN_PAGE_SIZE),
      )
    : 1;
  const validColumn =
    !selectedColumnKey ||
    columns.some(
      (column) =>
        placesColumnKey(column.phase, column.roundNumber) === selectedColumnKey,
    );
  const page = validColumn ? Math.min(requestedPage, pageCount) : 1;
  const visibleMatches = selectedColumn
    ? selectedColumn.matches.slice(
        (page - 1) * PLACES_COLUMN_PAGE_SIZE,
        page * PLACES_COLUMN_PAGE_SIZE,
      )
    : [];

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading bracket…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the bracket. Please try again.
      </Alert>
    );
  }
  if (selectedColumn === null) {
    return <Typography color="text.secondary">No matches yet</Typography>;
  }

  const start = (page - 1) * PLACES_COLUMN_PAGE_SIZE + 1;
  const end = Math.min(
    page * PLACES_COLUMN_PAGE_SIZE,
    selectedColumn.matches.length,
  );
  return (
    <Stack spacing={1.5}>
      <Autocomplete
        getOptionLabel={placesColumnLabel}
        isOptionEqualToValue={(left, right) =>
          placesColumnKey(left.phase, left.roundNumber) ===
          placesColumnKey(right.phase, right.roundNumber)
        }
        onChange={(_, column) => {
          const next = new URLSearchParams(params);
          next.set("tab", "draws");
          next.set("draw_id", draw.draw_id);
          if (column)
            next.set(
              "column",
              placesColumnKey(column.phase, column.roundNumber),
            );
          else next.delete("column");
          next.delete("places_page");
          setParams(next);
        }}
        options={columns}
        renderInput={(params) => (
          <TextField {...params} label="Bracket section" />
        )}
        value={selectedColumn}
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography color="text.secondary" variant="body2">
          Showing {start}–{end} of {selectedColumn.matches.length} matches
        </Typography>
        {pageCount > 1 ? (
          <Pagination
            count={pageCount}
            onChange={(_, nextPage) => {
              const next = new URLSearchParams(params);
              next.set("tab", "draws");
              next.set("draw_id", draw.draw_id);
              next.set("places_page", String(nextPage));
              if (selectedColumn)
                next.set(
                  "column",
                  placesColumnKey(
                    selectedColumn.phase,
                    selectedColumn.roundNumber,
                  ),
                );
              setParams(next);
            }}
            page={page}
            shape="rounded"
            size="small"
          />
        ) : null}
      </Stack>
      <BracketMatchList matches={visibleMatches} />
    </Stack>
  );
}

function GroupToKnockoutDetail({ draw }: { draw: DrawResponse }) {
  return (
    <Stack spacing={3}>
      {draw.qualification_state === "blocked" && draw.qualification_blocker ? (
        <Alert
          severity="warning"
          role="status"
          aria-label="Qualification status"
        >
          {draw.qualification_blocker}
        </Alert>
      ) : null}
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
          Group stage
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
          Top {draw.advance_per_group} from each of {draw.group_count} groups
          advance when the server confirms qualification.
        </Typography>
        <DrawStandings drawId={draw.draw_id} />
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1.5 }}>
          Knockout bracket
        </Typography>
        <DrawBracket drawId={draw.draw_id} phase="knockout" />
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Signed integer → "+5" / "-2" / "0" (the API already computed the diff; we only
// surface its sign, never recompute it).
// ---------------------------------------------------------------------------

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

// ---------------------------------------------------------------------------
// round_robin → standings (getStandings). One table per group; the group label
// shows ONLY when there is more than one group. Rows are VERBATIM.
// ---------------------------------------------------------------------------

function DrawStandings({ drawId }: { drawId: string }) {
  const { data, isLoading, isError } = useDrawStandings(drawId);

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading standings…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the standings. Please try again.
      </Alert>
    );
  }

  const groups: GroupStandings[] = (data as StandingsResponse)?.groups ?? [];
  const showGroupLabel = groups.length > 1;

  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <StandingsGroupTable
          key={group.group_number}
          group={group}
          showLabel={showGroupLabel}
        />
      ))}
    </Stack>
  );
}

function StandingsGroupTable({
  group,
  showLabel,
}: {
  group: GroupStandings;
  showLabel: boolean;
}) {
  const rows: StandingRow[] = group.rows ?? [];
  const head = {
    py: 0.5,
    px: 0.5,
    fontSize: "0.75rem",
    color: "text.secondary",
  };
  return (
    <Box
      component="section"
      aria-label={showLabel ? `Pool ${poolLetter(group.group_number)}` : "Standings"}
    >
      <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
        {showLabel ? `Pool ${poolLetter(group.group_number)}` : "Standings"}
      </Typography>
      <TableContainer>
        <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
          <TableHead>
            <TableRow>
              <TableCell scope="col" sx={{ ...head, width: 24 }}>
                #
              </TableCell>
              <TableCell scope="col" sx={head}>
                Player
              </TableCell>
              <TableCell scope="col" align="right" sx={head}>
                W–L
              </TableCell>
              <TableCell scope="col" align="right" sx={head}>
                Games
              </TableCell>
              <TableCell scope="col" align="right" sx={head}>
                Pts ±
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StandingRowCells key={row.entry_id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Group 1 → "A"; beyond Z falls back to the number. */
function poolLetter(groupNumber: number): string {
  return groupNumber >= 1 && groupNumber <= 26
    ? String.fromCharCode(64 + groupNumber)
    : String(groupNumber);
}

/**
 * One standings row, rendered VERBATIM. The rank is row.rank (the SERVER value),
 * never the array index. W-L is a SINGLE combined cell ("3-0") — deliberately
 * not two bare integer cells — so the only bare integer in the row is the rank.
 * Doubles teams list one player per line; the public handle the server gives
 * belongs to the entry's primary (first-named) player. The viewer's own row is
 * highlighted. The display_name falls back to "—", never the entry uuid.
 */
function StandingRowCells({ row }: { row: StandingRow }) {
  const { myEntryIds } = useContext(DrawView);
  const mine = myEntryIds.has(row.entry_id);
  // The server sends the entry's primary player (name + handle) only.
  const name = row.display_name?.trim();
  const cell = {
    py: 0.75,
    px: 0.5,
    fontSize: "0.8125rem",
    verticalAlign: "top",
    fontVariantNumeric: "tabular-nums",
    color: mine ? "primary.main" : "text.primary",
    fontWeight: mine ? 700 : 400,
  } as const;
  const handle = row.public_handle?.replace(/^#/, "");
  // Each numeric cell renders with a trailing space so adjacent integers stay
  // separable in the table's concatenated textContent.
  return (
    <TableRow aria-current={mine ? "true" : undefined}>
      <TableCell sx={cell}>{row.rank}</TableCell>
      <TableCell sx={{ ...cell, maxWidth: 0, width: "55%" }}>
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || "—"}
          {name && handle ? (
            <Box
              component="span"
              sx={{ color: mine ? "inherit" : "text.secondary" }}
            >
              {` #${handle}`}
            </Box>
          ) : null}
        </Box>
      </TableCell>
      <TableCell align="right" sx={cell}>{`${row.wins}-${row.losses} `}</TableCell>
      <TableCell align="right" sx={cell}>
        {`${row.games_won}–${row.games_lost} `}
      </TableCell>
      <TableCell align="right" sx={cell}>{`${signed(row.points_diff)} `}</TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Elimination formats → bracket (getBracket) as single-column match cards.
// Single elimination gets the round switch; other phases list their matches.
// The winner is shown bold inside a "Winner" group — never by repeating the
// winner's name as separate text.
// ---------------------------------------------------------------------------

function DrawBracket({ drawId, phase }: { drawId: string; phase?: string }) {
  const { data, isLoading, isError } = useDrawBracket(drawId);
  const { myEntryIds, mode } = useContext(DrawView);

  if (isLoading) {
    return (
      <Typography color="text.secondary" role="status">
        Loading bracket…
      </Typography>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" role="alert">
        We couldn&apos;t load the bracket. Please try again.
      </Alert>
    );
  }

  const matches = (data ?? []).filter(
    (match) => phase === undefined || match.phase === phase,
  );
  if (mode === "results") return <RecentResults matches={matches} />;
  return (
    <DrawRounds matches={matches} myEntryIds={myEntryIds} />
  );
}

/** Completed matches, latest round first, as the same cards as Draws. */
function RecentResults({ matches }: { matches: BracketMatchResponse[] }) {
  const { myEntryIds } = useContext(DrawView);
  const done = matches.filter(isPlayedResult);
  const totalRounds = matches.reduce(
    (last, match) => Math.max(last, match.round_number),
    0,
  );
  return (
    <Box component="section" aria-label="Recent results">
      <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, mb: 1 }}>
        Recent results
      </Typography>
      <DrawRounds
        matches={done}
        myEntryIds={myEntryIds}
        totalRounds={totalRounds}
        newestFirst
        stacked
        empty="No results yet"
      />
    </Box>
  );
}

/**
 * A result is a completed match between two entries: unplayed matches and
 * byes (one side empty) are not results.
 */
function isPlayedResult(match: BracketMatchResponse): boolean {
  return (
    match.status === "completed" &&
    match.entry1_id != null &&
    match.entry2_id != null
  );
}

function BracketMatchList({
  matches: allMatches,
  empty,
}: {
  matches: BracketMatchResponse[];
  empty?: string;
}) {
  const { myEntryIds, mode } = useContext(DrawView);
  // Results (every draw type) lists only played matches.
  const matches =
    mode === "results" ? allMatches.filter(isPlayedResult) : allMatches;
  if (matches.length === 0) {
    return (
      <Typography color="text.secondary">
        {empty ?? (mode === "results" ? "No results yet" : "No matches yet")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {matches.map((m) => (
        <BracketMatchCard
          key={m.match_id}
          match={m}
          mine={
            (m.entry1_id != null && myEntryIds.has(m.entry1_id)) ||
            (m.entry2_id != null && myEntryIds.has(m.entry2_id))
          }
        />
      ))}
    </Stack>
  );
}
